'use client';

import {
  convertCurrencyToCents,
  convertToCurrencyValue,
} from '@/utils/format-currency';
import { handleQueryString } from '@/utils/queryParamsUtil';
import CloseIcon from '@mui/icons-material/Close';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import MonetaryInput from '../MonetaryInput';
import SearchableCheckboxFilter from '../SearchableCheckboxFilter';
import { FilterListOfCardsProps, Option } from './FilterListOfCards.types';
import style from './style.module.css';
import { UNIQUE_FILTERS, CARD_FILTERS } from './FilterListOfCards.constants';
import {
  getBenefits,
  getCardFinancialInstitutions,
} from '@/services/creditCard/resources';
import { mapBenefits, mapBanks } from './FilterListOfCards.utils';
import { useTranslations } from 'next-intl';

let timeoutMonetaryValue;

const FilterListOfCards = ({
  initialFiltersList = [],
  countryCode,
}: FilterListOfCardsProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<Option[]>(initialFiltersList);
  const [benefits, setBenefits] = useState<Option[]>([]);
  const [banks, setBanks] = useState<Option[]>([]);

  async function loadBenefits() {
    const benefitsResponse = await getBenefits({ country: countryCode });
    const mappedBenefits = mapBenefits(benefitsResponse);
    setBenefits(mappedBenefits as Option[]);
  }

  async function loadBanks() {
    const banksResponse = await getCardFinancialInstitutions({
      country: countryCode,
    });
    const mappedBanks = mapBanks(banksResponse);
    setBanks(mappedBanks as Option[]);
  }
  const t = useTranslations('Index');

  const addActiveFilter = (option: Option) => {
    setLoading(true);
    setFilters((prevBenefits) => {
      const isUnique = UNIQUE_FILTERS.includes(option.category);
      const previous = prevBenefits || [];
      if (isUnique) {
        return [
          ...previous.filter(({ category }) => category !== option.category),
          option,
        ];
      }
      return [...previous, option];
    });
    const queryParams = handleQueryString(
      option.category === 'minIncomeCeil' ? 'set' : 'append',
      new URLSearchParams(searchParams),
      option.category,
      option.value
    );

    const newRoute = `${pathname}?${queryParams}`;
    router.push(newRoute);
  };

  const removeActiveFilter = (option: Option) => {
    setLoading(true);
    setFilters((prevBenefits) => {
      const isUnique = UNIQUE_FILTERS.includes(option.category);
      if (isUnique) {
        return prevBenefits.filter(
          ({ category }) => category !== option.category
        );
      }
      return prevBenefits.filter((filter) => filter.value !== option.value);
    });

    const queryParams = new URLSearchParams(searchParams);
    const filterValues = queryParams.getAll(option.category);
    const newFilterValues = filterValues.filter(
      (value) => value !== option.value
    );

    queryParams.delete(option.category);
    newFilterValues.forEach((value) => {
      queryParams.append(option.category, value);
    });

    const newRoute = `${pathname}?${queryParams}`;
    router.push(newRoute);
  };

  const clearAllFilters = () => {
    setLoading(true);
    setFilters([]);
    router.push(pathname);
  };

  const handleMinIncomeCeil = (value: string) => {
    if (timeoutMonetaryValue) window.clearTimeout(timeoutMonetaryValue);

    timeoutMonetaryValue = window.setTimeout(() => {
      const cleanValue = +value.replace(/\D/g, '');

      if (!cleanValue) {
        removeActiveFilter({
          value: convertCurrencyToCents(value),
          label: value,
          category: 'minIncomeCeil',
        });
        return;
      }
      addActiveFilter({
        value: convertCurrencyToCents(value),
        label: value,
        category: 'minIncomeCeil',
      });
    }, 1500);
  };

  const handleOptionLabel = (option: Option) => {
    const optionLabel = {
      bank: option.label,
      benefit: option.label,
      minIncomeCeil: convertToCurrencyValue(option.value),
    };
    return optionLabel[option.category];
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [isAnyFilterRendered, setIsAnyFilterRendered] = useState(false);

  useEffect(() => {
    const renderedFilters = filters.some((option) =>
      CARD_FILTERS.includes(option.category)
    );
    setIsAnyFilterRendered(renderedFilters);
  }, [filters]);

  useEffect(() => {
    loadBenefits();
    loadBanks();
  }, []);

  // route change completed
  useEffect(() => {
    setLoading(false);
  }, [searchParams]);

  return (
    <div className={style.containerFilterCards}>
      <div className={style.contentActiveFilter}>
        <p className={style.titleFilter}>{t('filter.creditCard.filterBy')}</p>
        <div className={style.contentActiveFilterTags}>
          {filters?.map((option) => {
            const shouldRender = CARD_FILTERS.includes(option.category);
            if (!shouldRender) return;
            return (
              <div className={style.listItem} key={option.value}>
                {handleOptionLabel(option)}
                <span
                  role="button"
                  className={style.buttonRemove}
                  onClick={() => removeActiveFilter(option)}>
                  {mounted ? <CloseIcon /> : null}
                </span>
              </div>
            );
          })}
        </div>
        <div className={style.contentActiveFilterClear}>
          {isAnyFilterRendered && (
            <p className={style.clearAll} onClick={clearAllFilters}>
              {t('filter.creditCard.clearAll')}
            </p>
          )}
        </div>
      </div>
      {mounted ? (
        <div className={style.contentFilterOfCards}>
          <SearchableCheckboxFilter
            selectedOptions={filters}
            onOptionDeselect={removeActiveFilter}
            onOptionSelect={addActiveFilter}
            placeholderSeach={t('filter.creditCard.select')}
            titleSearch={t('filter.creditCard.bank')}
            name="bank"
            allOptions={banks}
            disabled={!banks.length || loading}
          />

          <SearchableCheckboxFilter
            selectedOptions={filters}
            onOptionDeselect={removeActiveFilter}
            onOptionSelect={addActiveFilter}
            allOptions={benefits}
            placeholderSeach={t('filter.creditCard.select')}
            titleSearch={t('filter.creditCard.benefit')}
            name="benefit"
            disabled={!benefits.length || loading}
          />

          <MonetaryInput
            titleInputSelect={t('filter.creditCard.minimumIncome')}
            placeholder={t('filter.creditCard.value')}
            onChange={handleMinIncomeCeil}
            name="monetaryValue"
            value={convertToCurrencyValue(
              filters?.find(({ category }) => category === 'minIncomeCeil')
                ?.value || ''
            )}
            disabled={loading}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FilterListOfCards;
